import React, { useState, useEffect } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MdBloodtype } from "react-icons/md";
import "./charts.css";
import axios from 'axios';

const BLOOD_COLORS = {
  "A+": "#e11d48",
  "A-": "#fda4af",
  "B+": "#be123c",
  "B-": "#fecdd3",
  "O+": "#9f1239",
  "O-": "#ffe4e6",
  "AB+": "#f43f5e",
  "AB-": "#fff1f2",
  "Unknown": "#8b8e94ff",
  "default": "#ff4d4d"
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="custom-tooltip" style={{ borderLeft: `4px solid #ff4d4d` }}>
        <p className="tooltip-label" style={{ color: '#1f2937' }}>Blood Group: {data.name}</p>
        <p className="tooltip-value">
          <span className="tooltip-value-label">Donors: </span>
          <span className="tooltip-value-number" style={{ color: '#ff4d4d', fontWeight: '700' }}>{data.count}</span>
        </p>
      </div>
    );
  }
  return null;
};

export default function BloodGroupChart({ college }) {
  const [donorData, setDonorData] = useState([]);
  const port = process.env.REACT_APP_SERVER_PORT;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await axios.get(port + `count-by-blood-group?College=${college}`);
        
        const mergedGroups = {};
        Object.entries(result.data).forEach(([key, value]) => {
          if (!key) return;
          let name = key.trim();
          if (name.toLowerCase() === 'unknown' || name.toLowerCase() === 'u') {
            name = "Unknown";
          }
          mergedGroups[name] = (mergedGroups[name] || 0) + value;
        });

        const actualResult = Object.entries(mergedGroups).map(([name, count]) => ({
          name,
          count
        }));
        
        // Sort blood groups in a standard logical order for display
        const order = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-", "Unknown"];
        actualResult.sort((a, b) => {
          const idxA = order.indexOf(a.name);
          const idxB = order.indexOf(b.name);
          if (idxA === -1) return 1;
          if (idxB === -1) return -1;
          return idxA - idxB;
        });

        setDonorData(actualResult);
      } catch (error) {
        console.error("Error fetching blood group data:", error);
      }
    };

    fetchData();
  }, [college, port]);

  const totalDonors = donorData.reduce((sum, item) => sum + (item.count || 0), 0);

  return (
    <div className="chart-col">
      <div className="graph-card">
        {/* Custom Card Header */}
        <div className="graph-card-header">
          <div className="graph-card-title-container">
            <div className="graph-icon-wrapper" style={{ backgroundColor: '#ffebeb' }}>
              <MdBloodtype className="graph-header-icon" style={{ color: '#ff0000' }} />
            </div>
            <span className="graph-card-title" style={{ color: '#ff0000' }}>Blood Group Distribution</span>
          </div>
          <div className="graph-card-legend">
            <span className="legend-dot" style={{ borderColor: '#ff0000' }}></span>
            <span className="legend-text">Count per Group</span>
          </div>
        </div>

        {/* Graph wrapper */}
        <div className="chart-wrapper" style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {donorData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={donorData}
                margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="barRedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff4d4d" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#b91c1c" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaeaea" />
                <XAxis 
                  dataKey="name" 
                  tickLine={false}
                  axisLine={{ stroke: '#eaeaea' }}
                  tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                />
                <YAxis 
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.02)' }} />
                <Bar 
                  dataKey="count" 
                  fill="url(#barRedGradient)"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={45}
                >
                  {donorData.map((entry, index) => {
                    const specificColor = BLOOD_COLORS[entry.name] || BLOOD_COLORS.default;
                    return <Cell key={`cell-${index}`} fill={specificColor} opacity={0.95} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>No Blood Group Data Available</div>
          )}
        </div>

        {/* Metrics/Legend Section */}
        <div className="metrics-container" style={{ background: '#fff5f5', marginTop: '10px' }}>
          <div className="metric-box">
            <div className="metric-details" style={{ alignItems: 'center' }}>
              <span className="metric-label">Total Groups</span>
              <span className="metric-value" style={{ color: '#ff0000' }}>{donorData.length}</span>
            </div>
          </div>
          <div className="metric-divider" style={{ backgroundColor: '#ffe1e1' }}></div>
          <div className="metric-box">
            <div className="metric-details" style={{ alignItems: 'center' }}>
              <span className="metric-label">Total Donors</span>
              <span className="metric-value" style={{ color: '#ff0000' }}>{totalDonors}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
